const BASE = process.env.REACT_APP_API_URL || 'http://localhost:5062'

function normalizeImageUrl(url) {
  if (!url) return url
  if (url.startsWith('http')) return url
  return `${BASE}${url.startsWith('/') ? '' : '/'}${url}`
}

function getToken() {
  return localStorage.getItem('token')
}

async function apiFetch(path, options = {}) {
  const token = getToken()
  const res = await fetch(`${BASE}${path}`, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...options.headers,
    },
  })

  if (res.status === 401) {
    localStorage.removeItem('token')
    window.location.href = '/login'
    throw new Error('UNAUTHORIZED')
  }
  if (!res.ok) {
    const err = await res.json().catch(() => ({}))
    throw new Error(err.detail ?? err.title ?? 'API Error')
  }
  if (res.status === 204) return null
  return res.json()
}

// Auth
export const login = (data) => apiFetch('/api/auth/login', { method: 'POST', body: JSON.stringify(data) })

// Businesses
export async function getBusinesses(params = {}) {
  const data = await apiFetch('/api/businesses?' + new URLSearchParams(params))
  if (!data?.items) return data
  return {
    ...data,
    items: data.items.map(b => ({ ...b, firstImage: normalizeImageUrl(b.firstImage) })),
  }
}
export async function getBusinessById(id) {
  const b = await apiFetch(`/api/businesses/by-id/${id}`)
  if (!b) return b
  return { ...b, images: (b.images ?? []).map(img => ({ ...img, url: normalizeImageUrl(img.url) })) }
}
export const createBusiness = (data) => apiFetch('/api/businesses', { method: 'POST', body: JSON.stringify(data) })
export const updateBusiness = (id, data) => apiFetch(`/api/businesses/${id}`, { method: 'PUT', body: JSON.stringify(data) })
export const deleteBusiness = (id) => apiFetch(`/api/businesses/${id}`, { method: 'DELETE' })
export const setFeatured = (id, value) => apiFetch(`/api/businesses/${id}/featured`, { method: 'PATCH', body: JSON.stringify({ value }) })
export const setSponsored = (id, value) => apiFetch(`/api/businesses/${id}/sponsored`, { method: 'PATCH', body: JSON.stringify({ value }) })
export const setOurPick = (id, value) => apiFetch(`/api/businesses/${id}/ourpick`, { method: 'PATCH', body: JSON.stringify({ value }) })

// Business Images
export async function uploadBusinessImages(businessId, files) {
  const form = new FormData()
  files.forEach(f => form.append('files', f))
  const res = await fetch(`${BASE}/api/businesses/${businessId}/images`, {
    method: 'POST',
    headers: { Authorization: `Bearer ${getToken()}` },
    body: form,
  })
  if (!res.ok) throw new Error('Upload failed')
  return res.json()
}
export const deleteBusinessImage = (businessId, imageId) => apiFetch(`/api/businesses/${businessId}/images/${imageId}`, { method: 'DELETE' })
export const reorderBusinessImages = (businessId, ids) => apiFetch(`/api/businesses/${businessId}/images/reorder`, { method: 'PATCH', body: JSON.stringify({ ids }) })

// Categories
export const getAllCategories = () => apiFetch('/api/categories?activeOnly=false')
export const createCategory = (data) => apiFetch('/api/categories', { method: 'POST', body: JSON.stringify(data) })
export const updateCategory = (id, data) => apiFetch(`/api/categories/${id}`, { method: 'PUT', body: JSON.stringify(data) })
export const deleteCategory = (id) => apiFetch(`/api/categories/${id}`, { method: 'DELETE' })

// Services
export const getAllServices = () => apiFetch('/api/services')
export const createService = (data) => apiFetch('/api/services', { method: 'POST', body: JSON.stringify(data) })
export const updateService = (id, data) => apiFetch(`/api/services/${id}`, { method: 'PUT', body: JSON.stringify(data) })
export const deleteService = (id) => apiFetch(`/api/services/${id}`, { method: 'DELETE' })
export const addServiceFeature = (id, data) => apiFetch(`/api/services/${id}/features`, { method: 'POST', body: JSON.stringify(data) })
export const removeServiceFeature = (id, fid) => apiFetch(`/api/services/${id}/features/${fid}`, { method: 'DELETE' })

// Pricing
export const getPricingPlans = () => apiFetch('/api/pricing')
export const createPricingPlan = (data) => apiFetch('/api/pricing', { method: 'POST', body: JSON.stringify(data) })
export const updatePricingPlan = (id, data) => apiFetch(`/api/pricing/${id}`, { method: 'PUT', body: JSON.stringify(data) })
export const deletePricingPlan = (id) => apiFetch(`/api/pricing/${id}`, { method: 'DELETE' })
export const addPlanFeature = (id, data) => apiFetch(`/api/pricing/${id}/features`, { method: 'POST', body: JSON.stringify(data) })
export const removePlanFeature = (id, fid) => apiFetch(`/api/pricing/${id}/features/${fid}`, { method: 'DELETE' })

// FAQ
export const getFaqItems = () => apiFetch('/api/faq')
export const createFaqItem = (data) => apiFetch('/api/faq', { method: 'POST', body: JSON.stringify(data) })
export const updateFaqItem = (id, data) => apiFetch(`/api/faq/${id}`, { method: 'PUT', body: JSON.stringify(data) })
export const deleteFaqItem = (id) => apiFetch(`/api/faq/${id}`, { method: 'DELETE' })

// Contact
export const getContactMessages = (params = {}) => apiFetch('/api/contact?' + new URLSearchParams(params))
export const markContactRead = (id) => apiFetch(`/api/contact/${id}/read`, { method: 'PATCH' })

// Files
export async function uploadFile(file, entityType, entityId) {
  const form = new FormData()
  form.append('file', file)
  if (entityType) form.append('entityType', entityType)
  if (entityId) form.append('entityId', String(entityId))
  const res = await fetch(`${BASE}/api/files/upload`, {
    method: 'POST',
    headers: { Authorization: `Bearer ${getToken()}` },
    body: form,
  })
  if (!res.ok) throw new Error('Upload failed')
  return res.json()
}

// Locations
export const getLocations = () => apiFetch('/api/locations')
export const createLocation = (data) => apiFetch('/api/locations', { method: 'POST', body: JSON.stringify(data) })
export const deleteLocation = (id) => apiFetch(`/api/locations/${id}`, { method: 'DELETE' })

// Users
export const getUsers = () => apiFetch('/api/users')
export const createUser = (data) => apiFetch('/api/users', { method: 'POST', body: JSON.stringify(data) })
export const updateUser = (id, data) => apiFetch(`/api/users/${id}`, { method: 'PUT', body: JSON.stringify(data) })
export const deleteUser = (id) => apiFetch(`/api/users/${id}`, { method: 'DELETE' })
export const getRoles = () => apiFetch('/api/roles')
export const createRole = (data) => apiFetch('/api/roles', { method: 'POST', body: JSON.stringify(data) })
export const updateRole = (id, data) => apiFetch(`/api/roles/${id}`, { method: 'PUT', body: JSON.stringify(data) })
export const deleteRole = (id) => apiFetch(`/api/roles/${id}`, { method: 'DELETE' })

// Auth Settings
export const changePassword = (data) => apiFetch('/api/auth/change-password', { method: 'POST', body: JSON.stringify(data) })

// Site Settings (social media + contact info)
export const getSiteSettings    = ()     => apiFetch('/api/settings')
export const updateSiteSettings = (data) => apiFetch('/api/settings', { method: 'PUT', body: JSON.stringify(data) })

// Stats
export const getStats = () => apiFetch('/api/stats')
